import React from 'react'
import { observer } from 'mobx-react-lite'
import AppBar from '@mui/material/AppBar'
import Toolbar from '@mui/material/Toolbar'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import variables from 'variables/variables'
import useWindowSize from 'reusable/hooks/useWindowSize'
import CircularProgress from '@mui/material/CircularProgress'
import GapIcon from '/images/The Gap_Logo_Horizontal.svg'
import GapIconWhite from '/images/The_Gap_Icon_White.svg'
import { themeConfig } from '@gap/shared/theme'
import ProfileMenu from 'features/main/main-router/org-guard/main-container/profile-menu/ProfileMenu'
import CloseOutlined from '@mui/icons-material/CloseOutlined'
import IconButton from '@mui/material/IconButton'
import { useThemeStore } from 'theme/ThemeStore'
import { AnimateHeight } from '../mui/transition/AnimateHeight'
import ProgressDots from 'reusable/progress-dots/ProgressDots'
import { motion, AnimatePresence } from 'framer-motion'

export interface PlatformContainerProps {
   loading?: boolean
   loadingMessage?: string
   children?: React.ReactNode
   appBarBackground?: 'white' | 'primary'
   contentCentered?: boolean
   includeProfileMenu?: boolean
   variant?: 'plain' | 'tinted'
   onClose?: () => any
   hideAppBar?: boolean
   secondaryContent?: React.ReactNode
   progress?: {
      step: number
      totalSteps: number
   } | undefined
}

export const PlatformContainer = observer(function PlatformContainer(props: PlatformContainerProps) {
   const { screenLayout } = useWindowSize()
   const themeStore = useThemeStore()

   const appBarBackground = props.appBarBackground || themeStore.dark ? 'primary' : 'white'

   const appBarSx = appBarBackground === 'white' ? { background: '#fff', boxShadow: 'none', borderBottom: `1px solid ${themeConfig.primary[200]}` } : {}
   const gapIcon = appBarBackground === 'white' ? GapIcon : GapIconWhite
   const variantBackground = props.variant === 'tinted' ? {
      backgroundColor: themeConfig.light.background.alt,
   } : {}

   const { width } = useWindowSize()

   return <Stack sx={{
      height: '100dvh',
      flexDirection: 'row',
      alignItems: 'stretch',
      justifyContent: 'stretch',
      ...variantBackground,
      ...(themeStore.backgroundImage ? {
         backgroundImage: themeStore.backgroundImage,
         backgroundRepeat: 'no-repeat',
         backgroundSize: 'cover',
         backgroundPosition: 'center',
      } : {})
   }}>
      <Stack sx={{ flexGrow: 1, overflowY: 'auto' }}>
         <AnimateHeight sx={{ flexShrink: 0 }} duration={.2}>
            {props.hideAppBar ? undefined : (
               <AppBar sx={{ ...appBarSx }} position="static">
                  <Toolbar sx={{ display: 'flex', ...variantBackground, borderColor: themeConfig.primary['800'] }}>
                     <Box sx={{ flex: 1, ml: '-6px' }}>
                        <a
                           href="https://thegaphq.com/"
                           target="_blank"
                           rel="noopener noreferrer"
                           style={{ display: 'inline-block' }}
                        >
                           <img src={gapIcon} height="32" alt="The Gap" style={{ display: 'block' }} />
                        </a>
                     </Box>
                     {props.progress && <ProgressDots totalSteps={props.progress.totalSteps} index={props.progress.step} />}
                     {props.includeProfileMenu && <ProfileMenu />}
                     {props.onClose && <IconButton onClick={props.onClose}>
                        <CloseOutlined sx={{ cursor: 'pointer' }} />
                     </IconButton>}
                  </Toolbar>
               </AppBar>
            )}
         </AnimateHeight>
         <Stack spacing={2} sx={{
            p: screenLayout === 'mobile' ? 0.5 : variables.contentPadding,
            flexGrow: 1,
            flexShrink: 1,
            ...(props.contentCentered ? { justifyContent: 'center', alignItems: 'center' } : {})
         }}>
            <Stack sx={{ justifyContent: 'center', alignItems: 'center', flexGrow: 1 }} spacing={2}>
               {props.loading && <>
                  <Typography variant='body1'>
                     {props.loadingMessage && (props.loadingMessage[props.loadingMessage.length - 1] === '.' ? props.loadingMessage : `${props.loadingMessage}...`)}
                     {!props.loadingMessage && 'Loading...'}
                  </Typography>
                  <CircularProgress size="32px" sx={{ color: themeStore.dark ? 'white' : undefined }} />
               </>}
               {!props.loading && props.children}
            </Stack>
         </Stack>
         <AnimatePresence>
            {props.secondaryContent && width <= themeConfig.breakpoints.values.desktop && <Stack
               component={motion.div}
               initial={{ x: '100%', opacity: 0 }}
               animate={{ x: 0, opacity: 1 }}
               exit={{ x: '100%', opacity: 0 }}
               transition={{ type: 'spring', stiffness: 300, damping: 30 }}
               sx={{
                  backgroundColor: themeConfig.primary[800],
                  color: 'white',
                  p: 4,
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
               }}
            >
               {props.secondaryContent}
            </Stack>}
         </AnimatePresence>
      </Stack>
      <AnimatePresence>
         {props.secondaryContent && width > themeConfig.breakpoints.values.desktop && <Stack
            component={motion.div}
            initial={{ x: '100%', opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: '100%', opacity: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            sx={{
               width: '38%',
               backgroundColor: themeConfig.primary[800],
               color: 'white',
               p: 4,
               alignItems: 'center',
               justifyContent: 'center',
            }}
         >
            {props.secondaryContent}
         </Stack>
         }
      </AnimatePresence>
   </Stack>
})
